/* eslint-disable react-hooks/exhaustive-deps */
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import _ from "underscore";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import {
    AncillarySubtype,
    getRatePlan,
    getTotalToBePaid,
    IGetRatePlanReturn,
    useCouponQueryObserver,
    useRatePlanQueryObserver,
    IGetCompanyReturn,
    getCompanyData,
    getMobilePrefixes,
    useCheckAffiliatedQueryObserver,
    useCompanyQueryObserver,
    getTotalRentalDays,
    getCustomer,
    RESPONSE_TYPE_SERVICE,
    RESPONSE_CHECK_CODE,
    getCompany
} from "../../../api/services";
import {
    CreditType,
    FirstInvoiceRecipient,
    InvoiceType,
    LegalFormCode,
    MOBILITY_STORE_ALL,
    PaymentTiming,
    ProductCategory,
    ReactQueryKey,
    RentalType
} from "../../../types";
import {
    LOCALE_DATE_TIME_FORMAT,
    getCarGroupCode,
    setBookingOwnerPropertyCompanyCustomer,
    setInvoicePropertyCompanyByICompany,
    setInvoicePropertyPersonCustomer,
    LOCALE_ISO_DATE_TIME_FORMAT,
    setBookingOwnerPropertyPersonCustomer,
    setInvoicePropertyCompanyByICustomer
} from "../../../utils";
import {
    accordionExpandedState,
    carsAvailabilitiesParamsState,
    customerBillingDataB2B,
    isBusinessContinuityMode,
    isPersonBAState,
    ratePlanOfferIDState,
    stepState,
    totalRentalDaysState
} from "../recoil";
import { AccordionsExpanded, IFormValues, InsertBookingStep } from "../types";
import dealerAndTenant from "../../../../pricing/common/components/DealerAndTenant";

const StateManager = () => {

    const { t } = useTranslation('booking');
    const {i18n} = useTranslation();

    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const step = useRecoilValue(stepState);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);
    const setTotalRentalDays = useSetRecoilState(totalRentalDaysState);
    const setCarsAvailabilitiesParams = useSetRecoilState(carsAvailabilitiesParamsState);
    const setIsPersonBA = useSetRecoilState(isPersonBAState);
    const setAccordionExpanded = useRecoilCallback(({ set }) => (accordionName: keyof AccordionsExpanded, value: boolean) => {
        set(accordionExpandedState(accordionName), value);
    });
    const setCustomerBillingDataB2B = useSetRecoilState(customerBillingDataB2B);
    const [ratePlanOfferID, setRatePlanOfferID] = useRecoilState(ratePlanOfferIDState);

    const formContext = useFormContext<IFormValues>();

    const formState = useFormState<IFormValues>();

    const basicDetails = useWatch<IFormValues, "basicDetails">({
        name: 'basicDetails'
    });
    const extraServices = useWatch<IFormValues, "extraServices">({
        name: 'extraServices'
    });
    const firstDriver = useWatch<IFormValues, "firstDriver">({
        name: 'firstDriver'
    });

    const queryClient = useQueryClient();
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const couponQuery = useCouponQueryObserver({tenant: tenant as string, dealer: dealer as string});
    const ratePlanQuery = useRatePlanQueryObserver({tenant: tenant as string, dealer: dealer as string});
    const checkAffiliatedQuery = useCheckAffiliatedQueryObserver({tenant: tenant as string, dealer: dealer as string});
    const companyQuery = useCompanyQueryObserver({segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string});

    useEffect(() => {
        if (
            basicDetails.pickupDate === null ||
            basicDetails.dropoffDate === null ||
            !basicDetails.pickupDate.isValid() ||
            !basicDetails.dropoffDate.isValid() ||
            basicDetails.pickupDate.isAfter(basicDetails.dropoffDate)
        ) {
            setTotalRentalDays(0);
        }
        else {
            calculateTotRentalDays();
        }
    }, [basicDetails.pickupDate, basicDetails.dropoffDate]);

    const calculateTotRentalDays = async () => {
        if(basicDetails.pickupDate === null ||
            basicDetails.dropoffDate === null) {
            return;
        }

        const totalRentalDays = await getTotalRentalDays(
            {pickupDate: basicDetails.pickupDate.format(LOCALE_DATE_TIME_FORMAT),
                dropoffDate: basicDetails.dropoffDate.format(LOCALE_DATE_TIME_FORMAT)
            }, tenant ?? "");
        setTotalRentalDays(totalRentalDays);
    }

    useEffect(() => {
        const {
            pickupLocation,
            dropoffLocation,
            pickupDate,
            dropoffDate,
            carGroup,
            lockGroup,
            customerCode,
            rentalType,
            mileageIncluded
        } = basicDetails;
        if (
            pickupLocation &&
            dropoffLocation &&
            pickupDate &&
            dropoffDate &&
            carGroup &&
            mileageIncluded &&
            ratePlanQuery.data

        ) {

            const ratesId: string[] = []
            ratePlanQuery.data
            .mileageList.filter(ml => ml.value == mileageIncluded)
            .filter(ml => ml.products?.filter(p => p.product?.category?.code == ProductCategory.POA && p.product?.paymentTiming == PaymentTiming.PAY_ON_ARRIVAL)
            .forEach(p => ratesId.push(p.rateId?.toString())));

            formContext.trigger(['basicDetails.pickupDate', 'basicDetails.dropoffDate'])
                .then(valid => {
                    if (valid) {
                        const productQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: rentalType, tenant: tenant as string, dealer: dealer as string}]);

                        setCarsAvailabilitiesParams({
                            pickupDate: pickupDate.toISOString(),
                            dropoffDate: dropoffDate.toISOString(),
                            pickupLocation: pickupLocation,
                            dropoffLocation: dropoffLocation,
                            carGroupCode: getCarGroupCode(carGroup),
                            lockGroup: lockGroup,
                            customerCode: customerCode || null,
                            segment: productQuery?.segmentCode || '',
                            products: productQuery?.products || [],
                            rates: ratesId || [],
                            tenant: tenant as string,
                            dealer: dealer as string
                        });

                    }
                })
            return () => {
                setCarsAvailabilitiesParams(undefined);
            }
        }
    }, [
        basicDetails.pickupLocation,
        basicDetails.pickupDate,
        basicDetails.dropoffLocation,
        basicDetails.dropoffDate,
        basicDetails.carGroup,
        basicDetails.lockGroup,
        basicDetails.mileageIncluded,
        ratePlanQuery.data
    ]);

    useEffect(() => {
        const {
            pickupLocation,
            dropoffLocation,
            pickupDate,
            dropoffDate,
            carGroup,
            customerCode,
            bookingDateTime
        } = basicDetails;

        const productsQuery = companyQuery.data;
        if (
            pickupLocation &&
            dropoffLocation &&
            pickupDate &&
            dropoffDate &&
            carGroup &&
            productsQuery &&
            (basicDetails.rentalType === RentalType.RETAIL || customerCode)

            && tenant && dealer

        ) {
            formContext.trigger(['basicDetails.pickupDate', 'basicDetails.dropoffDate'])
                .then( async (valid) => {
                    if (valid) {
                        
                        const dmsAssociatedOperator = globalData?.user?.mobilityStores.map(
                            ((elem:any) => {
                                return elem.sapId;
                            })
                        );

                        const ratePlan = await queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.RatePlan, {tenant: tenant as string, dealer: dealer as string}],
                            queryFn: () => getRatePlan({
                                pickupDate: pickupDate.format(LOCALE_DATE_TIME_FORMAT),
                                dropoffDate: dropoffDate.format(LOCALE_DATE_TIME_FORMAT),
                                pickupLocation: pickupLocation.id,
                                dropoffLocation: dropoffLocation.id,
                                carGroupCode: getCarGroupCode(carGroup),
                                products: productsQuery?.products,
                                businessAgreement: customerCode ?? null,
                                offerId: ratePlanOfferID,
                                firstEdit: false,
                                profileIdList: dmsAssociatedOperator?.includes(MOBILITY_STORE_ALL) || dmsAssociatedOperator?.includes(pickupLocation.id) ? globalData?.user?.profiles
                                    .map((p: any) => (
                                        p.id
                                    )) : [],
                                requestDate: businessContinuityMode ? bookingDateTime?.toISOString() || undefined : undefined,
                                tenant: tenant as string,
                                dealer: dealer as string
                            }),
                            staleTime: 0
                        });

                        const ancillary = formContext.getValues('extraServices.ancillary');

                        let newAncillaryObject: Record<string, boolean | number> = {} ;

                        ratePlan.ancillaries.filter(a => ['A', 'Z'].includes(a.ancillaryDetails[0].sellType)).map( a => {
                            const code = a.subtype === AncillarySubtype.AdditionalDrivers ? a.subtype : a.ancillaryCode;
                            newAncillaryObject[code] = (a.maxUnits > 1 || a.mileageNeeded) ? 1 : true;
                        })

                        Object.keys(ancillary).map( elem => {
                            if(ratePlan.ancillaries
                                .some( a => !['A', 'Z'].includes(a.ancillaryDetails[0].sellType) && (a.ancillaryCode === elem ||
                                    (elem === AncillarySubtype.AdditionalDrivers && a.subtype === AncillarySubtype.AdditionalDrivers)))) {
                                newAncillaryObject[elem] = ancillary[elem];
                            }
                        })
                        formContext.setValue('extraServices.ancillary', newAncillaryObject);

                        if(!!ratePlan.offerId && ratePlanOfferID != ratePlan.offerId)
                            setRatePlanOfferID(ratePlan.offerId);
                    }
                })
            return () => {
                // cleanup: nessun resetQueries necessario, la queryKey include i parametri
                // quindi al cambio date/location la cache si invalida naturalmente
            }
        }
    }, [
        basicDetails.pickupLocation,
        basicDetails.pickupDate,
        basicDetails.dropoffLocation,
        basicDetails.dropoffDate,
        basicDetails.carGroup,
        basicDetails.customerCode,
        basicDetails.bookingDateTime,
        companyQuery.data,

        tenant,
        dealer
    ]);
    useEffect(() => {
        const {
            pickupLocation,
            dropoffLocation,
            pickupDate,
            dropoffDate,
            mileageIncluded
        } = basicDetails;
        const ratePlan = ratePlanQuery.data;

        if (
            pickupLocation &&
            dropoffLocation &&
            pickupDate &&
            dropoffDate &&
            mileageIncluded &&
            ratePlan &&
            (!couponQuery?.data || couponQuery?.data?.code === RESPONSE_CHECK_CODE.SUCCESS)

            && tenant && dealer

            /* &&
            step === InsertBookingStep.AVAILABILITY_PRICE */
        ) {
            formContext.trigger(['basicDetails.pickupDate', 'basicDetails.dropoffDate'])
                .then((valid) => {
                    if (valid) {
                        const discountType = formContext.getValues('extraServices.discountType');
                        const promoCode = discountType === 'promo' ?
                            formContext.getValues('extraServices.discountCode') :
                            formContext.getValues('extraServices.fcaBankCardNumber');
                        queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.TotalToBePaid, {tenant: tenant as string, dealer: dealer as string}],
                            queryFn: () => {
                                const rateDetailIds = ratePlan.mileageList.find(m => m.value === mileageIncluded)?.products.map(p => p.rateId) ?? [];
                                if (rateDetailIds.length === 0) {
                                    return null;
                                }
                                return getTotalToBePaid({
                                pickupDate: pickupDate.toISOString(),
                                dropOffDate: dropoffDate.toISOString(),
                                rateDetailIds,
                                ancillaryDetailIds: Object
                                    .entries(formContext.getValues('extraServices.ancillary'))
                                    .reduce((arr: any[], [key, value]: [string, number | boolean]) => {
                                        switch (typeof value) {
                                            case 'number':
                                                const mileageNeeded: boolean = ratePlanQuery.data?.ancillaries.find(a => a.ancillaryCode === key)?.mileageNeeded ?? false;
                                                const ancillaryCode = key === AncillarySubtype.AdditionalDrivers ?
                                                    (ratePlanQuery.data?.ancillaries.find(a => a.subtype === key)?.ancillaryCode ?? '') :
                                                    key;
                                                value !== 0 && arr.push({
                                                    ancillaryCode: ancillaryCode,
                                                    units: mileageNeeded ? null : value,
                                                    mileage: mileageNeeded ? value : null
                                                });
                                                break;
                                            case 'boolean':
                                                value && arr.push({
                                                    ancillaryCode: key,
                                                    units: 1,
                                                    mileage: null
                                                });
                                                break;
                                        }
                                        return arr;
                                    }, []),
                                promo: promoCode !== undefined && !! couponQuery?.data && couponQuery?.data?.codeServiceType !== RESPONSE_TYPE_SERVICE.leisure ? [{
                                    code: promoCode,
                                    type: couponQuery.data.codeServiceType
                                }] : [],
                                affiliationCode: checkAffiliatedQuery?.data && checkAffiliatedQuery?.data?.valid  && !!checkAffiliatedQuery?.data.genVendorCode ?
                                    checkAffiliatedQuery?.data.genVendorCode :
                                    null,
                                offerId: ratePlan.offerId,
                                discountOverpriceSelected: extraServices.discountOverprice || undefined,
                                tenant: tenant as string,
                                dealer: dealer as string,
                                language: i18n?.language ? i18n?.language?.toLowerCase() : 'it'
                            });
                            },
                            staleTime: 0
                        });
                    }
                });
            return () => {
                // cleanup: nessun resetQueries necessario, la queryKey include i parametri
            };
        }
    }, [
        basicDetails.mileageIncluded,
        JSON.stringify(extraServices.ancillary),
        couponQuery.data,
        extraServices.discountOverprice,
        checkAffiliatedQuery.data,
        ratePlanQuery.data,
        tenant,
        dealer
    ]);

    /**
     * AUTOMATIC FETCH OF VERIFY BA
     */
    useEffect(() => {
        const {
            customerCode,
            pickupDate,
            rentalType,
            pickupLocation,
            dropoffLocation
        } = basicDetails;

        if(
            customerCode &&
            pickupDate &&
            rentalType == RentalType.CORPORATE &&
            tenant && dealer
        )
        {
            formContext.trigger('basicDetails.pickupDate')
                .then(async valid => {

                        if(valid) {

                            const company = await queryClient.fetchQuery({
                                queryKey: [ReactQueryKey.Product, {segment: rentalType, tenant: tenant as string, dealer: dealer as string}],
                                queryFn: () => getCompany(customerCode, pickupDate.toISOString(), tenant as string, dealer as string)
                            });

                            if(company && company.locations && !!pickupLocation && !company.locations.find(location => location.id == pickupLocation.id)) {
                                formContext.setValue('basicDetails.pickupLocation', null);
                            }

                            if(company && company.locations && !!dropoffLocation && !company.locations.find(location => location.id == dropoffLocation.id)) {
                                formContext.setValue('basicDetails.dropoffLocation', null);
                            }

                        }
                    }
                );
        }

    }, [
        basicDetails.pickupDate,
        basicDetails.rentalType,
        tenant, dealer
    ]);

    useEffect(() => {
        if (!tenant || !dealer) {
            return;
        }

        if (basicDetails.rentalType === RentalType.RETAIL) {
            // set booking owner from first driver
            for (const [key, value] of Object.entries(firstDriver)) {
                // @ts-ignore
                formContext.setValue((`bookingOwner.${key}`), value);
            }
        }
        else {
            const productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);

            if (basicDetails.rentalType === RentalType.CORPORATE &&
                productAffiliatedQuery?.firstInvoiceRecipient !== FirstInvoiceRecipient.COMPANY &&
                productAffiliatedQuery?.creditType === CreditType.NO_CREDIT &&
                formContext.getValues('invoice.type') === InvoiceType.PERSON) {
                // set invoice person from first driver
                for (const [key, value] of Object.entries(firstDriver)) {
                    // @ts-ignore
                    formContext.setValue((`invoice.${key}`), value);
                }
            }
        }
    }, [firstDriver, tenant, dealer]);

    useEffect(() => {
        if (!formState.isSubmitting) {
            if (!_.isEmpty(formState.errors)) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.form'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
            console.log('errors', formState.errors);
            if (formState.errors.extraServices) {
                setAccordionExpanded('extraServices', true);
            }
            if (formState.errors.firstDriver) {
                setAccordionExpanded('firstDriver', true);
            }
            if (formState.errors.invoice) {
                setAccordionExpanded('invoice', true);
            }
            if (formState.errors.bookingOwner) {
                setAccordionExpanded('bookingOwner', true);
            }
        }
    }, [formState.isSubmitting]);

    /**
     * Change step
     */
    useEffect(() => {
        formContext.resetField('bookingOwner');
        formContext.resetField('invoice');

        // Navigate to step CUSTOMER_DETAILS
        if (step === InsertBookingStep.CUSTOMER_DETAILS) {
            if(basicDetails.rentalType === RentalType.CORPORATE) {
                companyData();
            } else {
                setIsPersonBA(false);
                // set booking owner from first driver
                for (const [key, value] of Object.entries(formContext.getValues('firstDriver'))) {
                    // @ts-ignore
                    formContext.setValue((`bookingOwner.${key}`), value);
                }
            }
        }
    }, [step]);

    const companyData = async () => {
        const productAffiliated = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: basicDetails.rentalType, tenant: tenant as string, dealer: dealer as string}]);

        if(!!productAffiliated) {
            const firstInvoiceRecipient = productAffiliated.firstInvoiceRecipient;
            const params = {
                companyId: productAffiliated.companyId,
                firstInvoiceRecipient: firstInvoiceRecipient,
                creditType: productAffiliated.creditType
            }
            const ownerCompanyResponse = await getCompanyData(params);

            const mobilePrefixes = await queryClient.fetchQuery({
                queryKey: [ReactQueryKey.MobilePrefixes],
                queryFn: () => getMobilePrefixes()
            });

            setIsPersonBA(((productAffiliated.firstInvoiceRecipient == FirstInvoiceRecipient.PERSON) || 
                (productAffiliated.firstInvoiceRecipient == FirstInvoiceRecipient.CUSTOMER && 
                productAffiliated.creditType == CreditType.NO_CREDIT && 
                ownerCompanyResponse.bookingOwnerPerson !== null)));
                
            if(firstInvoiceRecipient == FirstInvoiceRecipient.PERSON) {  

                // set booking person owner
                const bookingOwner = setBookingOwnerPropertyPersonCustomer(ownerCompanyResponse.bookingOwnerPerson);
                for (const [key, value] of Object.entries(bookingOwner)) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }

                if(ownerCompanyResponse.bookingOwnerPerson.mobilePrefix) {
                    const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === ownerCompanyResponse.bookingOwnerPerson.mobilePrefix);
                    formContext.setValue('bookingOwner.mobilePrefix', foundMobilePrefix ?? null);
                }

                // set billing form - in questo ramo non è un oggetto che si utilizza
                setCustomerBillingDataB2B({
                    customerId: '',
                    sapId: '',
                    companyName: '',
                    vatNumber: '',
                    vatNumberCountry: '',
                    fiscalCode: '',
                    siren: '',
                    siret: '',
                    country: '',
                    city: '',
                    streetName: '',
                    streetNumber: '',
                    postalCode: '',
                    certifiedEmail: '',
                    email: '',
                    invoiceCode: '',
                    uuid: '',
                    telephone: '',
                    mobile: '',
                    mobilePrefix: '',
                    province: '',
                    linkedToBusinessAgreement: false,
                    legalEntity: null,
                    publicAuthority: '',
                    paIdNumber: ''

                });
                
                formContext.setValue('invoice.type', InvoiceType.PERSON);

                //eventualmente ripristinare l'if-else ed aggiungere l'errore di configurazione
                if(productAffiliated?.creditType !== CreditType.NO_CREDIT) {
                    const billingOwner = setInvoicePropertyPersonCustomer(ownerCompanyResponse.billingOwnerPerson);
                    for (const [key, value] of Object.entries(billingOwner)) {
                        // @ts-ignore
                        formContext.setValue((`${key}`), value);
                    }

                    if(ownerCompanyResponse.billingOwnerPerson.mobilePrefix) {
                        const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === ownerCompanyResponse.billingOwnerPerson.mobilePrefix);
                        formContext.setValue('invoice.mobilePrefix', foundMobilePrefix ?? null);
                    }
                }

            } else {

                if(!!ownerCompanyResponse.bookingOwner) {
                    // set booking owner
                    const bookingOwner = setBookingOwnerPropertyCompanyCustomer(ownerCompanyResponse.bookingOwner);
                    for (const [key, value] of Object.entries(bookingOwner)) {
                        // @ts-ignore
                        formContext.setValue((`${key}`), value);
                    }

                    if(ownerCompanyResponse.bookingOwner.mobilePrefix) {
                        const mobilePrefixes = await queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.MobilePrefixes],
                            queryFn: () => getMobilePrefixes()
                        });

                        const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === ownerCompanyResponse.bookingOwner.mobilePrefix);
                        formContext.setValue('bookingOwner.mobilePrefix', foundMobilePrefix ?? null);
                    }
            }

                // set billing form
                setCustomerBillingDataB2B(ownerCompanyResponse.billingOwner);
            }

            if(productAffiliated?.creditType === CreditType.NO_CREDIT) {
                if(productAffiliated?.firstInvoiceRecipient === FirstInvoiceRecipient.CUSTOMER) {
                    if(ownerCompanyResponse.bookingOwnerPerson !== null) {
                        // set booking person owner
                        const bookingOwner = setBookingOwnerPropertyPersonCustomer(ownerCompanyResponse.bookingOwnerPerson);
                        for (const [key, value] of Object.entries(bookingOwner)) {
                            // @ts-ignore
                            formContext.setValue((`${key}`), value);
                        }

                        if(ownerCompanyResponse.bookingOwnerPerson.mobilePrefix) {
                            const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === ownerCompanyResponse.bookingOwnerPerson.mobilePrefix);
                            formContext.setValue('bookingOwner.mobilePrefix', foundMobilePrefix ?? null);
                        }

                    } 
                    else {
                        formContext.setValue('invoice.type', InvoiceType.PERSON);
                        const fistDriverId = formContext.getValues('firstDriver.id');
                        if(!!fistDriverId) {
                            let customer = await getCustomer(fistDriverId);
                            for (const [key, value] of Object.entries(setInvoicePropertyPersonCustomer(customer))) {
                                // @ts-ignore
                                formContext.setValue((`${key}`), value);
                            }
    
                            formContext.setValue('invoice.email', formContext.getValues('firstDriver.email'));
                            formContext.setValue('invoice.mobile', formContext.getValues('firstDriver.mobile'));
                            formContext.setValue('invoice.mobilePrefix', formContext.getValues('firstDriver.mobilePrefix'));
                        } else {
                            for (const [key, value] of Object.entries(formContext.getValues('firstDriver'))) {
                                // @ts-ignore
                                formContext.setValue((`invoice.${key}`), value);
                            }
                        }
                    }
                    
                }
                else if(productAffiliated?.firstInvoiceRecipient === FirstInvoiceRecipient.COMPANY) {
                    if(ownerCompanyResponse?.billingOwner?.legalEntity?.code === LegalFormCode.DITTA_INDIVIDUALE) {
                        formContext.setValue('invoice.type', InvoiceType.VAT_NUMBER_HOLDER);
                    } else {
                        formContext.setValue('invoice.type', InvoiceType.COMPANY);
                    }
                    const billingOwner = setInvoicePropertyCompanyByICompany(ownerCompanyResponse.billingOwner);
                    for (const [key, value] of Object.entries(billingOwner)) {
                        // @ts-ignore
                        formContext.setValue((`${key}`), value);
                    }
                }
            } else {
                if(productAffiliated?.firstInvoiceRecipient !== FirstInvoiceRecipient.PERSON) {
                    if(ownerCompanyResponse?.billingOwner?.legalEntity?.code === LegalFormCode.DITTA_INDIVIDUALE) {
                        formContext.setValue('invoice.type', InvoiceType.VAT_NUMBER_HOLDER);
                    } else {
                        formContext.setValue('invoice.type', InvoiceType.COMPANY);
                    }
                    const billingOwner = setInvoicePropertyCompanyByICompany(ownerCompanyResponse.billingOwner);
                    for (const [key, value] of Object.entries(billingOwner)) {
                        // @ts-ignore
                        formContext.setValue((`${key}`), value);
                    }
                }
            }
        }
    }
    
return <></>;
}

export default StateManager;
